import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import ExploreIcon from "@mui/icons-material/Explore";
import ForestIcon from "@mui/icons-material/Forest";
import HikingIcon from "@mui/icons-material/Hiking";
import HotelIcon from "@mui/icons-material/Hotel";
import LandscapeIcon from "@mui/icons-material/Landscape";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import PoolIcon from "@mui/icons-material/Pool";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import SpaIcon from "@mui/icons-material/Spa";
import TerrainIcon from "@mui/icons-material/Terrain";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { useNavigate } from "react-router-dom";
import usePopularTopics from "../../hooks/usePopularTopics";
import { useI18n } from "../../../i18n/I18nContext";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const ICONS = {
    DirectionsBike: DirectionsBikeIcon,
    Explore: ExploreIcon,
    Forest: ForestIcon,
    Hiking: HikingIcon,
    Hotel: HotelIcon,
    Landscape: LandscapeIcon,
    LocalCafe: LocalCafeIcon,
    LocalDining: LocalDiningIcon,
    Pool: PoolIcon,
    Restaurant: RestaurantIcon,
    RoomService: RoomServiceIcon,
    Spa: SpaIcon,
    Terrain: TerrainIcon,
    TravelExplore: TravelExploreIcon,
};

export function resolvePopularTopicImageUrl(pathOrUrl) {
    if (!pathOrUrl) return "/images/hero_map.jpg";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("/images/")) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

function fallbackImageUrl(topic) {
    const key = String(topic?.title || "").trim().toLowerCase();
    const map = {
        wandern: "/images/popular-topics/wandern.jpg",
        wellness: "/images/popular-topics/wellness.jpg",
        "e-bike": "/images/popular-topics/e-bike.jpg",
        skiregionen: "/images/popular-topics/skiregionen.jpg",
        gourmet: "/images/popular-topics/gourmet.jpg",
        ausflugsziele: "/images/popular-topics/ausflugsziele.jpg",
    };
    return map[key] || "/images/hero_map.jpg";
}

function localizedTopicTitle(topic, lang) {
    const raw = String(topic?.title || "").trim();
    const key = raw.toLowerCase();
    const labels = {
        wandern: { en: "Hiking", fr: "Randonnée", it: "Escursioni" },
        wellness: { en: "Wellness", fr: "Bien-être", it: "Wellness" },
        "e-bike": { en: "E-bike", fr: "E-bike", it: "E-bike" },
        skiregionen: { en: "Ski Regions", fr: "Régions de ski", it: "Regioni sciistiche" },
        gourmet: { en: "Gourmet", fr: "Gourmet", it: "Gourmet" },
        ausflugsziele: { en: "Excursions", fr: "Excursions", it: "Escursioni" },
    };
    return labels[key]?.[lang] || raw;
}

export default function HomePopularTopics({ limit = 6, showAllButton = true, title }) {
    const navigate = useNavigate();
    const { t, lang } = useI18n();
    const { topics, loading, error } = usePopularTopics({ includeInactive: false, withCounts: false });

    const rows = React.useMemo(() => (limit ? (topics || []).slice(0, limit) : (topics || [])), [topics, limit]);

    const runSearch = (topic) => {
        const query = (topic.search_query || topic.title || "").trim();
        const params = new URLSearchParams();
        if (query) params.set("q", query);
        navigate({ pathname: "/", search: params.toString() ? `?${params.toString()}` : "" });
    };

    if (error || (!loading && rows.length === 0)) return null;

    return (
        <Box sx={{ bgcolor: "background.paper", borderBottom: "1px solid #e5e7eb" }}>
            <Box
                sx={{
                    maxWidth: 1540,
                    mx: "auto",
                    px: { xs: 2, md: 4 },
                    py: { xs: 4, md: 5 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
                    <Typography component="h2" sx={{ fontSize: { xs: "1.4rem", md: "1.75rem" }, fontWeight: 800, color: "var(--domain-text)" }}>
                        {title || t("home.popularTopics.title")}
                    </Typography>
                    {showAllButton && (
                        <Button
                            endIcon={<ArrowForwardIcon fontSize="small" />}
                            onClick={() => navigate("/themen")}
                            sx={{ color: "var(--domain-text)", fontWeight: 700, textTransform: "none" }}
                        >
                            {t("home.popularTopics.viewAll")}
                        </Button>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <CircularProgress sx={{ color: "var(--domain-primary)" }} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                                lg: limit ? "repeat(6, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
                            },
                            gap: 2,
                        }}
                    >
                        {rows.map((topic) => {
                            const Icon = ICONS[topic.icon_name] || TravelExploreIcon;
                            return (
                                <Box
                                    key={topic.id}
                                    component="button"
                                    type="button"
                                    onClick={() => runSearch(topic)}
                                    sx={{
                                        position: "relative",
                                        minHeight: { xs: 190, md: 240 },
                                        border: 0,
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        p: 0,
                                        cursor: "pointer",
                                        color: "#fff",
                                        bgcolor: "var(--domain-text)",
                                        boxShadow: "0 10px 24px rgba(15,23,42,0.15)",
                                        transition: "transform 0.18s ease, box-shadow 0.18s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 16px 32px rgba(15,23,42,0.22)",
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={resolvePopularTopicImageUrl(topic.image_url || fallbackImageUrl(topic))}
                                        alt={topic.title}
                                        onError={(event) => {
                                            const fallback = fallbackImageUrl(topic);
                                            if (event.currentTarget.getAttribute("src") === fallback) return;
                                            event.currentTarget.src = fallback;
                                        }}
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            background: "linear-gradient(180deg, rgba(15,23,42,0.08) 0%, rgba(15,23,42,0.30) 42%, rgba(0,0,0,0.82) 100%)",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: 0,
                                            right: 0,
                                            bottom: 22,
                                            px: 1.5,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: "50%",
                                                display: "grid",
                                                placeItems: "center",
                                                bgcolor: "background.paper",
                                                color: "var(--domain-primary)",
                                                mb: 1.25,
                                                boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Typography sx={{ fontSize: "1rem", lineHeight: 1.2, fontWeight: 800, textAlign: "center", minHeight: 24 }}>
                                            {localizedTopicTitle(topic, lang)}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
